/**
 * Reassignment Page (Admin Only)
 * Reassign assets between customers and projects
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  SwapHoriz as SwapIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

import { useAppSelector } from '../../../store/hooks';
import AppBar from '../../elements/AppBar';
import PageBody from '../../elements/PageBody';
import PageTitle from '../../elements/PageTitle';
import useProjectSelector from '../../../hooks/useProjectSelector';
import * as api from '../../../services/api';

const steps = ['Select Source', 'Select Assets', 'Select Destination', 'Confirm'];

const ReassignmentPage = () => {
  const { customerList } = useAppSelector((state) => state.customer);
  const customers = customerList || [];
  const { ProjectSelectorComponent } = useProjectSelector();
  
  const [activeStep, setActiveStep] = useState(0);
  const [sourceCustomer, setSourceCustomer] = useState('');
  const [destCustomer, setDestCustomer] = useState('');
  const [destProject, setDestProject] = useState('');
  const [selectedAssets, setSelectedAssets] = useState([]);
  
  // Data from API
  const [assets, setAssets] = useState([]);
  const [destProjects, setDestProjects] = useState([]);
  
  // Loading states
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Error and feedback
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch assets when source customer changes
  const fetchSourceAssets = useCallback(async () => {
    if (!sourceCustomer) {
      setAssets([]);
      return;
    }
    
    setLoadingAssets(true);
    setError(null);
    
    try {
      const response = await api.getAssetsByCustomer(sourceCustomer);
      setAssets(response.items || []);
    } catch (err) {
      console.error('Error fetching assets:', err);
      setError('Failed to load assets');
      setAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  }, [sourceCustomer]);

  useEffect(() => {
    if (activeStep === 1) {
      fetchSourceAssets();
    }
  }, [activeStep, fetchSourceAssets]);

  // Fetch projects when destination customer changes
  const fetchDestProjects = useCallback(async () => {
    if (!destCustomer) {
      setDestProjects([]);
      return;
    }
    
    setLoadingProjects(true);
    
    try {
      const response = await api.getProjectsByCustomer(destCustomer);
      setDestProjects(response.items || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setDestProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [destCustomer]);

  useEffect(() => {
    if (destCustomer) {
      fetchDestProjects();
    }
  }, [destCustomer, fetchDestProjects]);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleAssetToggle = (assetId) => {
    setSelectedAssets((prev) => 
      prev.includes(assetId) 
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAssets.length === assets.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(assets.map(a => a.id));
    }
  };

  const handleConfirmReassignment = async () => {
    setSubmitting(true);
    setError(null);
    
    try {
      const result = await api.reassignAssets({
        assetIds: selectedAssets,
        sourceCustomerId: sourceCustomer,
        destinationCustomerId: destCustomer,
        destinationProjectId: destProject || null,
      });
      
      if (result.success) {
        setSnackbar({ 
          open: true, 
          message: `Successfully reassigned ${result.transferred} assets`, 
          severity: 'success' 
        });
        
        // Reset wizard
        setActiveStep(0);
        setSelectedAssets([]);
        setSourceCustomer('');
        setDestCustomer('');
        setDestProject('');
        setAssets([]);
      } else {
        setError(`Reassignment partially failed: ${result.errors?.join(', ') || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error reassigning assets:', err);
      setError(err.message || 'Failed to reassign assets');
    } finally {
      setSubmitting(false);
    }
  };

  const getAssetTypeName = (asset) => {
    return asset.assetTypeName || asset.type || 'Unknown';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />
      
      <PageBody>
        {/* Title Row */}
        <PageTitle>
          Reassign Assets
        </PageTitle>

        <Paper sx={{ p: 3, boxShadow: 'none', border: '1px solid #4A4847' }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step 0: Select Source */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Source Customer
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Source Customer</InputLabel>
                <Select
                  value={sourceCustomer}
                  label="Source Customer"
                  onChange={(e) => {
                    setSourceCustomer(e.target.value);
                    setSelectedAssets([]);
                  }}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="contained" 
                  onClick={handleNext}
                  disabled={!sourceCustomer}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 1: Select Assets */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Assets to Reassign
              </Typography>
              
              {loadingAssets ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : assets.length > 0 ? (
                <TableContainer sx={{ mt: 2, maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox 
                            checked={selectedAssets.length === assets.length && assets.length > 0}
                            indeterminate={selectedAssets.length > 0 && selectedAssets.length < assets.length}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>Asset ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assets.map((asset) => (
                        <TableRow key={asset.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedAssets.includes(asset.id)}
                              onChange={() => handleAssetToggle(asset.id)}
                            />
                          </TableCell>
                          <TableCell>{asset.uuid || asset.unitNumber || asset.id}</TableCell>
                          <TableCell>{asset.name || '-'}</TableCell>
                          <TableCell>{getAssetTypeName(asset)}</TableCell>
                          <TableCell>{asset.status || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No assets found for the selected customer.
                </Alert>
              )}
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={handleBack}>Back</Button>
                <Button 
                  variant="contained" 
                  onClick={handleNext}
                  disabled={selectedAssets.length === 0}
                >
                  Next ({selectedAssets.length} selected)
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 2: Select Destination */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Destination Customer
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Destination Customer</InputLabel>
                <Select
                  value={destCustomer}
                  label="Destination Customer"
                  onChange={(e) => {
                    setDestCustomer(e.target.value);
                    setDestProject('');
                  }}
                >
                  {customers
                    .filter((c) => c.id !== sourceCustomer)
                    .map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              
              {destCustomer && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Destination Project (optional)</InputLabel>
                  <Select
                    value={destProject}
                    label="Destination Project (optional)"
                    onChange={(e) => setDestProject(e.target.value)}
                    disabled={loadingProjects}
                  >
                    <MenuItem value="">
                      <em>None - Unassigned</em>
                    </MenuItem>
                    {destProjects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={handleBack}>Back</Button>
                <Button 
                  variant="contained" 
                  onClick={handleNext}
                  disabled={!destCustomer}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 3: Confirm */}
          {activeStep === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Confirm Reassignment
              </Typography>
              <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
                You are about to reassign {selectedAssets.length} assets. This action will transfer ownership and update all related records.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="subtitle2" color="text.secondary">From</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {customers.find(c => c.id === sourceCustomer)?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SwapIcon color="action" />
                  </Grid>
                  <Grid item xs={5}>
                    <Typography variant="subtitle2" color="text.secondary">To</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {customers.find(c => c.id === destCustomer)?.name}
                    </Typography>
                    {destProject && (
                      <Typography variant="body2" color="text.secondary">
                        Project: {destProjects.find(p => p.id === destProject)?.name}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <strong>{selectedAssets.length}</strong> assets will be transferred
                </Typography>
              </Paper>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={handleBack} disabled={submitting}>Back</Button>
                <Button 
                  variant="contained" 
                  color="success"
                  startIcon={submitting ? <CircularProgress size={20} /> : <CheckIcon />}
                  onClick={handleConfirmReassignment}
                  disabled={submitting}
                >
                  {submitting ? 'Processing...' : 'Confirm Reassignment'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </PageBody>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReassignmentPage;
